import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearParticipantsCourses, loadCoursesByParticipantId } from '../../../store/courseService';
import { selectAttendedStatusId } from '../../../store/dataSelectors';
import {
    loadAttendanceDetails,
    loadBloodTestEvents,
    loadMeasurementTypes,
    loadWellbeingDetailsForContractId
} from '../../../store/directusService';
import {
    clearParticipantAuditEventSearchData,
    loadParticipantAuditEvent
} from '../../../store/participantService';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    SELF_EMPLOYED_COACH,
    SUPERUSER
} from '../../../utils/userRoles';
import AuditWellbeingModal from '../../auditing/auditWellbeing/AuditWellbeingModal';
import Switch from '../../formElements/Switch';

import AlcoholConsumed from './alcoholConsumed/AlcoholConsumed';
import BloodGlucose from './bloodGlucose/BloodGlucose';
import BloodPressure from './bloodPressure/BloodPressure';
import BloodTest from './bloodTest/BloodTest';
import BMI from './bmi/BMI';
import HealthIncident from './healthIncident/HealthIncident';
import PhysicalActivity from './physicalActivity/PhysicalActivity';
import SignPosting from './signPosting/SignPosting';
import Smoking from './smoking/Smoking';
import Steps from './steps/Steps';
import T2DRHealthData from './t2drHealthData/T2DRHealthData';
import WaistMeasurements from './waistMeasurements/WaistMeasurements';

import app from '../../../app.module.css';
import local from './wellbeing.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const createRoles = [ADVISER, MANAGER, QUALITY, SELF_EMPLOYED_COACH, SUPERUSER];
const restrictedEditRoles = [QUALITY, SUPERUSER];
const deleteRoles = [MANAGER, QUALITY, SUPERUSER];

const requireLinkedModuleTypes = [
    't2dr health data',
    'bmi',
    'blood test',
    'health incident',
    'steps'
];
const requireMeasurementTypes = ['bmi', 'blood glucose', 'blood pressure'];
const requireBloodTestEvents = ['bmi', 'blood test'];

const Wellbeing = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [types, setTypes] = useState([]);
    const [isCoursesRequired, setIsCoursesRequired] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
    const [isCreateDisabled, setIsCreateDisabled] = useState(true);
    const [isRestrictedEditDisabled, setIsRestrictedEditDisabled] = useState(true);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const wellbeingDetails = useSelector(
        (state) => state.entities.directusService.wellbeingDetails
    );
    const participantsCourses = useSelector(
        (state) => state.entities.courseService.participantsCourses
    );
    const participantsCoursesMetaData = useSelector(
        (state) => state.entities.courseService.participantsCoursesMetaData
    );
    const loadingParticipantsCourses = useSelector(
        (state) => state.entities.courseService.loadingParticipantsCourses
    );
    const attendedStatusId = useSelector(selectAttendedStatusId);
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );
    const bloodTestEvents = useSelector((state) => state.entities.directusService.bloodTestEvents);

    // USEEFFECTS
    useEffect(() => () => cleanUp(), []);

    useEffect(() => {
        if ('id' in currentParticipant) {
            dispatch(loadWellbeingDetailsForContractId(currentParticipant.contractId));
        }
        if ('status' in currentParticipant) {
            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
            setIsCreateDisabled(!hasRole(createRoles, roles) || disableOnClosedStatus);
            setIsRestrictedEditDisabled(
                !hasRole(restrictedEditRoles, roles) || disableOnClosedStatus
            );
            setIsDeleteDisabled(!hasRole(deleteRoles, roles) || disableOnClosedStatus);
        }
    }, [currentParticipant?.id, currentParticipant?.status, currentParticipant?.contractId, roles]);

    useEffect(() => {
        if (!types.length || !currentParticipant?.id) return;
        const requireCourses = types.some((el) => requireLinkedModuleTypes.includes(el));
        if (requireCourses) {
            setIsCoursesRequired(true);
            dispatch(loadCoursesByParticipantId(currentParticipant.id, 0));
            !attendedStatusId && dispatch(loadAttendanceDetails());
        }

        const requireMeasurements = types.some((el) => requireMeasurementTypes.includes(el));
        if (requireMeasurements && measurementTypes?.length < 1) dispatch(loadMeasurementTypes());

        const requireEvents = types.some((el) => requireBloodTestEvents.includes(el));
        if (requireEvents && bloodTestEvents?.length < 1) dispatch(loadBloodTestEvents());
    }, [types, currentParticipant?.id]);

    useEffect(() => {
        if (!isCoursesRequired) return;
        if (
            'id' in currentParticipant &&
            !loadingParticipantsCourses &&
            participantsCourses.length > 0 &&
            'number' in participantsCoursesMetaData &&
            !participantsCoursesMetaData.last
        ) {
            dispatch(
                loadCoursesByParticipantId(
                    currentParticipant.id,
                    participantsCoursesMetaData.number + 1
                )
            );
        }
    }, [
        isCoursesRequired,
        participantsCoursesMetaData,
        participantsCourses,
        loadingParticipantsCourses,
        currentParticipant?.id
    ]);

    useEffect(() => {
        if (wellbeingDetails?.length < 1) return;
        setTypes(wellbeingDetails.map((el) => el.name.toLowerCase()));
    }, [wellbeingDetails]);

    // FNs
    const cleanUp = () => {
        dispatch(clearParticipantsCourses());
    };

    // EVENT HANDLERS
    const onSearchAuditItems = (e, id, section, field, url) => {
        e.stopPropagation();
        if (id) dispatch(loadParticipantAuditEvent(url, id, field));
        setShowAuditModal(true);
        setAuditLabel(`${section} - ${field}`);
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearParticipantAuditEventSearchData());
    };

    // RENDER
    return (
        <div className={app.fadein}>
            <div className={local.headingContent} data-testid="wellbeing">
                <Switch
                    id={'auditDisplayFields'}
                    label="Show Audit Fields"
                    onOff={true}
                    customClass="auditSwitch"
                    checked={isAuditable}
                    onChange={(e) => setIsAuditable(e.target.checked)}
                />
            </div>
            <div>
                {types.includes('signposting') && (
                    <SignPosting
                        participant={currentParticipant}
                        isDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                    />
                )}
                {types.includes('bmi') && (
                    <BMI
                        participant={currentParticipant}
                        isEditDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isCreateDisabled={isCreateDisabled}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                )}
                {types.includes('waist measurement') && (
                    <WaistMeasurements
                        participant={currentParticipant}
                        isDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                )}
                {types.includes('alcohol consumed') && (
                    <AlcoholConsumed
                        participant={currentParticipant}
                        isDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                )}
                {types.includes('physical activity') && (
                    <PhysicalActivity
                        participant={currentParticipant}
                        isDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                    />
                )}
                {types.includes('blood test') && (
                    <BloodTest
                        participant={currentParticipant}
                        isEditDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isCreateDisabled={isCreateDisabled}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                )}
                {types.includes('blood glucose') && (
                    <BloodGlucose
                        participant={currentParticipant}
                        isEditDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isCreateDisabled={isCreateDisabled}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                )}
                {types.includes('blood pressure') && (
                    <BloodPressure
                        participant={currentParticipant}
                        isEditDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isCreateDisabled={isCreateDisabled}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                )}
                {types.includes('health incident') && (
                    <HealthIncident
                        participant={currentParticipant}
                        isEditDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isCreateDisabled={isCreateDisabled}
                    />
                )}
                {types.includes('t2dr health data') && (
                    <T2DRHealthData
                        participant={currentParticipant}
                        isCreateDisabled={isCreateDisabled}
                        isEditDisabled={isRestrictedEditDisabled}
                    />
                )}
                {types.includes('smoking') && (
                    <Smoking
                        participant={currentParticipant}
                        isDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                    />
                )}
                {types.includes('steps') && (
                    <Steps
                        participant={currentParticipant}
                        isEditDisabled={isDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        isCreateDisabled={isCreateDisabled}
                    />
                )}
            </div>
            {showAuditModal && (
                <div id="auditWellbeingModal">
                    <AuditWellbeingModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </div>
    );
};

export default Wellbeing;
